import React from 'react'
import Page from 'ui/page'
import { Row, Col, Button, Card, CardHeader, CardBody } from 'reactstrap'
import styled from 'styled-components'
import { isMobile } from 'react-device-detect'
import SearchBox from 'containers/layouts/search_box'
import ReactCarousel from 'react-multi-carousel'
import ProductCard from 'containers/list/products/product_card'
import ReviewCard from 'ui/review_card'
import { useTranslation } from 'react-i18next'

import background from 'in_destinum/img/background'
import barcelonaTour from 'in_destinum/img/barcelona-tour'
import madridTour from 'in_destinum/img/madrid-tour'
import mexicoCityTour from 'in_destinum/img/mexico-city-tour'
import drawing from 'in_destinum/img/drawing'

const grid = {
  xl: {
    breakpoint: { max: 8000, min: 1200 },
    items: 5,
  },
  lg: {
    breakpoint: { max: 1200, min: 992 },
    items: 4,
  },
  md: {
    breakpoint: { max: 992, min: 768 },
    items: 3,
  },
  sm: {
    breakpoint: { max: 768, min: 576 },
    items: 2,
  },
  xs: {
    breakpoint: { max: 576, min: 0 },
    items: 1,
  },
}

const HomeCard = styled.figure.attrs({
  className: 'my-3'
})`
  position: relative;
  cursor: pointer;

  img {
    width: 100%;
    border-radius: 35px;
    object-fit: cover;
    height: 15em;
  }

  figcaption {
    color: white;
    border-radius: 35px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    text-shadow: 0px 0px 5px #000;
    transition: all 0.3s ease-out;

    &:hover {
      background: rgba(0, 0, 0, 0.2);
      border: 1px solid var(--bs-primary);
    }

    a {
      font-size: 2em;
      text-align: center;
      color: white;
      font-weight: 800;
    }
  }
`

const ExperienceCard = styled(Col)`
  display: flex;
  flex-direction: column;

  p {
    flex-grow: 1;
  }
`

const Headline = (props) => {
  const { i18n } = useTranslation()
  const {product_link, user} = props.menu_props

  return (
    <Row>
      <Col
        xs="12"
        sm="12"
        className="text-center"
        style={{ padding: '190px 0' }}
      >
        <div
          className="p-3 text-white"
          style={{
            textShadow: '1px 1px 2px #000',
            maxWidth: '700px',
            margin: 'auto'
          }}
        >
          <h1 className="fw-bold" style={{ fontSize: '3em' }}>
            {i18n.t('home.in_destinum_headline')}
          </h1>
          <div className="mt-3 mb-2" style={{ fontSize: '1.2em' }}>
            {i18n.t('home.in_destinum_herosearch_title')}
          </div>
          <div>
            <SearchBox productLink={product_link} user={user} wide/>
          </div>
        </div>
      </Col>
    </Row>
  )
}

const DestinationCards = ({ urls, user }) => {
  const { i18n } = useTranslation()
  const destinations = user.user_data.user.destinations

  return (
    <>
      <h2 className="text-center py-5">{i18n.t('home.our_destinations')}</h2>
      <Row className="row-cols-1 row-cols-sm-3 row-cols-lg-8">
        {destinations.map(({ name, slug, url, thumbnail_url }) => {
          const destinationUrl = urls.destination.replace(/ *\:[^)]*\: */g, slug)

          return (
            <Col key={name}>
              <HomeCard onClick={() => (window.location.href = destinationUrl)}>
                <img src={thumbnail_url} alt={name} />
                <figcaption className="shadow-lg">
                  <a href={destinationUrl}>{name}</a>
                </figcaption>
              </HomeCard>
            </Col>
          )
        })}
      </Row>
    </>
  )
}

const FeatureCards = (props) => {
  const {best_sellers, urls} = props
  const { i18n } = useTranslation()

  return (
    <Row>
      <Col>
        <Card className="rounded-5 text-center">
          <CardBody>
            <i className="fas fa-medal text-primary py-3" style={{fontSize: '4.5em'}}/>
            <h3>Top Experiences</h3>
            <div style={{fontSize: '1.2em'}}>
              We carefully select the best local providers to ensure your satisfaction.
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col>
        <Card className="rounded-5 text-center">
          <CardBody>
            <i className="fas fa-clock text-primary py-3" style={{fontSize: '4.5em'}}/>
            <h3>Save Time</h3>
            <div style={{fontSize: '1.2em'}}>
              Secure your tickets and skip the lines at the world’s most visited landmarks.
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col>
        <Card className="rounded-5 text-center">
          <CardBody>
            <i className="fa fa-user text-primary py-3" style={{fontSize: '4.5em'}}/>
            <h3>Book with Confidence</h3>
            <div style={{fontSize: '1.2em'}}>
              Cancel most of our activities for free up to 24 hours before they start.
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

const BestExperiences = (props) => {
  const {best_sellers, urls} = props
  const { i18n } = useTranslation()

  if (best_sellers?.products?.length <= 0) { return "" }

  return (
    <>
      <h2 className="text-center pb-5">{i18n.t('home.best_experiences')}</h2>
      <Row className="row-cols-1 row-cols-sm-3 row-cols-lg-5 g-4">
        {best_sellers?.products?.slice(0, 5).map((product, key) => (
          <ProductCard
            {...product}
            height="165"
            key={key}
            url={urls.service.replace(/ *\:[^)]*\: */g, product.slug)}
            urls={urls}
          />
        ))}
      </Row>
    </>
  )
}

const Reviews = () => {
  const { i18n } = useTranslation()

  return (
    <>
      <h2 className="text-center pb-5 fst-italic fw-light text-white">{i18n.t('home.reviews_title')}</h2>
      <Row>
        <Col className="d-flex justify-content-center">
          <ReviewCard/>
        </Col>
        <Col className="d-flex justify-content-center">
          <ReviewCard/>
        </Col>
      </Row>
    </>
  )
}

const Index = (props) => {
  return (
    <Page {...props} className="p-0">
      <div
        className={isMobile ? 'container' : 'container-fluid'}
        style={{
          background: `url(${background})`,
          backgroundBlendMode: 'multiply',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          backgroundSize: 'cover'
        }}
      >
        <Headline {...props} />
      </div>

      <div className="container py-3">
        <DestinationCards {...props} />
      </div>

      <div
        className="container-fluid mt-5 py-5 bg-light"
        style={{
          background: `url(${background})`,
          backgroundBlendMode: 'multiply',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          backgroundSize: 'cover'
        }}
      >
        <div className="container px-5">
          <FeatureCards {...props} />
        </div>
      </div>

      <div className="container-fluid py-5">
        <div className="container">
          <BestExperiences {...props} />
        </div>
      </div>

      <div className="container-fluid py-5" style={{background: '#52B6BC'}}>
        <div className="container">
          <Reviews {...props} />
        </div>
      </div>
    </Page>
  )
}

export default Index
